import React from 'react';
import styled from 'styled-components';
import {
  LayoutDashboard,
  Megaphone,
  Ticket,
  Users,
  Star,
  Wallet,
  Receipt,
  BarChart3,
  Settings,
  Store,
  UserCog,
  ShieldCheck,
  LogOut,
  CalendarClock,
  PartyPopper,
} from 'lucide-react';
import logoNeg from '../assets/logo_negative_mix.png';
import { ADMIN_NAV_GROUP_ORDER, AdminNavGroup, AdminNavItem, AdminSectionId } from '../utils/navigation';
import FeaturedBanner from '../components/FeaturedBanner';

const ICONS: Record<AdminSectionId, React.ElementType> = {
  dashboard: LayoutDashboard,
  promotions: Megaphone,
  experiences: PartyPopper,
  coupons: Ticket,
  customers: Users,
  reviews: Star,
  cashback: Wallet,
  billing: Receipt,
  reports: BarChart3,
  employees: UserCog,
  'access-profiles': ShieldCheck,
  settings: Settings,
  profile: Store,
  'business-hours': CalendarClock,
};

const Shell = styled.div`
  display: flex;
  height: 100vh;
  background: ${(p) => p.theme.background};
`;

const Sidebar = styled.aside`
  width: 260px;
  flex-shrink: 0;
  background: ${(p) => p.theme.navy};
  display: flex;
  flex-direction: column;
`;

const Brand = styled.div`
  padding: 1.25rem;
  border-bottom: 1px solid ${(p) => p.theme.navySoft};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;

  img.logo {
    width: 100%;
    max-width: 200px;
    height: auto;
    /* the asset is a white/orange lockup on black; screen drops the black into the navy */
    mix-blend-mode: screen;
  }

  .tag {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${(p) => p.theme.primary};
  }
`;

const Nav = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;
`;

const Group = styled.div`
  margin-bottom: 1.25rem;

  .title {
    padding: 0 1.25rem;
    margin-bottom: 0.4rem;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.4);
  }
`;

const Item = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 1.25rem;
  border: none;
  background: ${(p) => (p.$active ? p.theme.navySoft : 'transparent')};
  border-left: 3px solid ${(p) => (p.$active ? p.theme.primary : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : 'rgba(255,255,255,0.72)')};
  font-size: 0.9rem;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${(p) => p.theme.navySoft};
    color: #fff;
  }
`;

const Footer = styled.div`
  border-top: 1px solid ${(p) => p.theme.navySoft};
  padding: 0.75rem 1rem;
  /* nome do usuario e o botao de sair na mesma linha */
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .user {
    flex: 1;
    min-width: 0;
    padding: 0.5rem 0.25rem;
    display: flex;
    flex-direction: column;
    strong {
      font-size: 0.85rem;
      color: #fff;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    small {
      font-size: 0.72rem;
      color: rgba(255, 255, 255, 0.5);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

/**
 * Sair compacto: so o icone, a direita do nome do usuario (mesma linha),
 * para o rodape do sidemenu nao ocupar duas linhas.
 */
const LogoutButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  width: 34px;
  border: 1px solid ${(p) => p.theme.navySoft};
  border-radius: 8px;
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${(p) => p.theme.navySoft};
    color: #fff;
  }
`;

const Main = styled.main`
  flex: 1;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 1.5rem;
`;

interface MainLayoutProps {
  activeSection: AdminSectionId;
  onSectionChange: (section: AdminSectionId) => void;
  onLogout: () => void;
  merchantName?: string;
  userName?: string;
  navItems: AdminNavItem[];
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  activeSection,
  onSectionChange,
  onLogout,
  merchantName,
  userName,
  navItems,
  children,
}) => {
  const groups = ADMIN_NAV_GROUP_ORDER.map((group: AdminNavGroup) => ({
    group,
    items: navItems.filter((item) => item.group === group),
  })).filter((g) => g.items.length > 0);

  return (
    <Shell>
      <Sidebar>
        <Brand>
          <img className="logo" src={logoNeg} alt="Kardume Urbano" />
          <div className="tag">Administração</div>
        </Brand>
        <Nav>
          {groups.map(({ group, items }) => (
            <Group key={group}>
              <div className="title">{group}</div>
              {items.map((item) => {
                const Icon = ICONS[item.section];
                return (
                  <Item
                    key={item.section}
                    $active={activeSection === item.section}
                    onClick={() => onSectionChange(item.section)}
                  >
                    <Icon size={18} />
                    {item.label}
                  </Item>
                );
              })}
            </Group>
          ))}
        </Nav>
        <Footer>
          <div className="user">
            <strong>{merchantName || 'Minha loja'}</strong>
            <small>{userName || 'Lojista'}</small>
          </div>
          <LogoutButton type="button" onClick={onLogout} title="Sair" aria-label="Sair">
            <LogOut size={18} />
          </LogoutButton>
        </Footer>
      </Sidebar>
      <Main>
        <Content>
          {/* Card 262 - o aviso de destaque vem antes do conteúdo de qualquer
              tela: quem entra para responder uma avaliação não passa pela
              Dashboard, e é enquanto o destaque está no ar que saber dele
              vale alguma coisa. */}
          <FeaturedBanner />
          {children}
        </Content>
      </Main>
    </Shell>
  );
};

export default MainLayout;
